/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { debounce, isEmpty, isEqual } from 'lodash'
import React, { Component, Fragment } from 'react'

import { EuiCallOut, EuiListGroup, EuiSkeletonText, EuiSpacer } from '@elastic/eui'

import type { BasicFailedReplyElement } from '@modules/cloud-api/v1/types'
import type { SliderInstanceType } from '@modules/ui-types/sliders'
import { FormattedMessage } from '@modules/i18n'
import { CuiAlert } from '@modules/cui/Alert'
import { getStatus } from '@modules/utils/error'

import { getUpsertVersion } from '@/lib/stackDeployments/selectors/creates'
import { isSliderInstanceTypeSupportedInPlatform } from '@/lib/sliders/support'
import { getSliderPrettyName } from '@/lib/sliders/messages'

import { getValidationWarnings, decorateWarningsWithSliderType } from '../../lib/dryRunWarnings'

import { aggregateWarningsByMessage } from './warningAggregator'

import type { ReactElement } from 'react'
import type { Props } from './types'

class StackDeploymentUpdateDryRunWarnings extends Component<Props> {
  componentDidMount(): void {
    this.executeDryRun()
  }

  componentDidUpdate(prevProps: Props): void {
    const { deployment } = this.props

    if (isEqual(deployment, prevProps.deployment)) {
      return
    }

    this.executeDryRunDebounced()
  }

  render(): JSX.Element | null {
    const { deployment, updateDeploymentDryRunRequest } = this.props

    if (
      updateDeploymentDryRunRequest.error &&
      getStatus(updateDeploymentDryRunRequest.error) !== 400
    ) {
      return this.renderBetweenSpacers(
        <CuiAlert type='warning'>{updateDeploymentDryRunRequest.error}</CuiAlert>,
      )
    }

    /* In this particular case, we *do* want to render a spinner whenever we're fetching,
     * because the response changes based on the user-provided plan input
     */
    if (updateDeploymentDryRunRequest.inProgress) {
      return this.renderBetweenSpacers(<EuiSkeletonText lines={2} />)
    }

    const warnings = getValidationWarnings({
      updateDeploymentDryRunRequest,
    })

    if (isEmpty(warnings)) {
      return null
    }

    const warningsWithSliderType = decorateWarningsWithSliderType(warnings)

    // Aggregate similar warnings that differ only by topology tier
    const aggregatedWarnings = this.aggregateWarningsByMessage(warningsWithSliderType)

    return this.renderBetweenSpacers(
      <EuiCallOut
        title={
          <FormattedMessage
            id='stack-deployment-update-warnings.callout-bad-request-title'
            defaultMessage='Your changes cannot be applied'
          />
        }
        color='warning'
      >
        <EuiListGroup
          maxWidth={false}
          wrapText={true}
          size='s'
          style={{ margin: 0, padding: 0 }}
          listItems={aggregatedWarnings.map(({ message, sliderInstanceType }) => {
            const useSliderInstanceType =
              sliderInstanceType && isSliderInstanceTypeSupportedInPlatform(sliderInstanceType)
            return {
              label: (
                <FormattedMessage
                  id='stack-deployment-update-warnings.warning'
                  defaultMessage='{sliderPrettyName}{separator}{message}'
                  values={{
                    message,
                    separator: useSliderInstanceType ? ` - ` : undefined,
                    sliderPrettyName: useSliderInstanceType ? (
                      <FormattedMessage
                        {...getSliderPrettyName({
                          sliderInstanceType: sliderInstanceType!,
                          version: getUpsertVersion({ deployment }),
                        })}
                      />
                    ) : undefined,
                  }}
                />
              ),
            }
          })}
        />
      </EuiCallOut>,
    )
  }

  renderBetweenSpacers(children: ReactElement): JSX.Element {
    const { spacerBefore, spacerAfter } = this.props

    return (
      <Fragment>
        {spacerBefore && <EuiSpacer size='m' />}

        {children}

        {spacerAfter && <EuiSpacer size='m' />}
      </Fragment>
    )
  }

  executeDryRun(): void {
    const { resetUpdateDeploymentDryRun, updateDeploymentDryRun } = this.props

    resetUpdateDeploymentDryRun()
    updateDeploymentDryRun()
  }

  executeDryRunDebounced = debounce(this.executeDryRun, this.props.debounceTimeout || 0) // eslint-disable-line react/sort-comp

  /**
   * Delegate to the extracted aggregateWarningsByMessage function
   * @param warnings Array of warning objects
   * @returns Array of aggregated warnings with count property
   */
  aggregateWarningsByMessage(
    warnings: Array<BasicFailedReplyElement & { sliderInstanceType?: SliderInstanceType }>,
  ) {
    return aggregateWarningsByMessage(warnings)
  }
}

export default StackDeploymentUpdateDryRunWarnings
